import type { DueGroup } from '../types';

// ── Due-date grouping — shared by Purchase.tsx and Sales.tsx ────────────────
// Real business logic (not per-page UI), so — unlike this codebase's usual
// per-page-duplicated-helper convention — it lives in one place, same
// reasoning as ReturnModal.tsx/utils/ledger.ts being shared instead of
// duplicated. A line item's due date is always DERIVED, never typed
// directly: invoiceDate + (line.terms ?? invoice.terms). 'separate' mode
// groups line items by that computed date VALUE, not by line-item
// identity, so items landing on the same date share one payable DueGroup.

// Local calendar day — never toISOString(), which is UTC: in any timezone
// ahead of UTC (India, Europe…) local midnight is still the previous day in
// UTC, so every computed date (due dates, today, filter ranges) came out a day early.
function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDaysToDate(base: string, n: number): string {
  if (!base) return toDateStr(new Date());
  const d = new Date(base + 'T00:00:00');
  if (isNaN(d.getTime())) return toDateStr(new Date());
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}
function round2(n: number) { return Math.round((n + Number.EPSILON) * 100) / 100; }

export function lineDueDate(baseDate: string, invoiceTerms: number, lineTerms?: number): string {
  const terms = lineTerms != null && lineTerms > 0 ? lineTerms : invoiceTerms;
  return addDaysToDate(baseDate, terms);
}

export function dueGroupIdFor(dueDate: string): string {
  return `dg_${dueDate}`;
}

// Groups `lines` by their computed due date, summing each group's amount.
// Returns groups sorted ascending by date, each freshly seeded (no
// payments/credits yet) — callers write this straight onto a new or
// re-saved invoice's `dueGroups` field.
export function computeDueGroups<T>(
  lines: T[],
  baseDate: string,
  invoiceTerms: number,
  termsOf: (line: T) => number | undefined,
  amountOf: (line: T) => number,
): DueGroup[] {
  const byDate = new Map<string, number>();
  lines.forEach(line => {
    const d = lineDueDate(baseDate, invoiceTerms, termsOf(line));
    byDate.set(d, (byDate.get(d) ?? 0) + amountOf(line));
  });
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dueDate, amountUSD]) => ({
      id: dueGroupIdFor(dueDate),
      dueDate,
      amountUSD: round2(amountUSD),
      paidUSD: 0,
      creditedUSD: 0,
      paymentStatus: 'unpaid' as const,
    }));
}

// The invariant that contains this feature's blast radius: an invoice's
// own paidUSD/creditedUSD/paymentStatus/dueDate must always be an
// accurate aggregate of its dueGroups, so every other part of the app
// that only reads those invoice-level scalars (the ledger, Trial
// Balance, Balance Sheet, Dashboard's KPI row, etc.) keeps working
// unmodified. Call this after any write that changes a dueGroups array.
export function aggregateFromDueGroups(groups: DueGroup[]): {
  paidUSD: number; creditedUSD: number; paymentStatus: 'paid' | 'partial' | 'unpaid'; dueDate: string;
} {
  const paidUSD     = round2(groups.reduce((s, g) => s + g.paidUSD, 0));
  const creditedUSD = round2(groups.reduce((s, g) => s + g.creditedUSD, 0));
  const allPaid  = groups.length > 0 && groups.every(g => g.paymentStatus === 'paid');
  const anyMoved = groups.some(g => g.paidUSD > 0 || g.creditedUSD > 0);
  const paymentStatus = allPaid ? 'paid' : anyMoved ? 'partial' : 'unpaid';
  const dueDate = groups.length > 0 ? [...groups].sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0].dueDate : '';
  return { paidUSD, creditedUSD, paymentStatus, dueDate };
}

// A single DueGroup's own outstanding/status, same formula as an
// invoice's outstanding() helpers elsewhere in this app.
export function dueGroupOutstanding(g: DueGroup): number {
  return Math.max(0, g.amountUSD - g.paidUSD - g.creditedUSD);
}
export function dueGroupStatus(g: DueGroup): 'paid' | 'partial' | 'unpaid' {
  if (dueGroupOutstanding(g) <= 0.005) return 'paid';
  if (g.paidUSD > 0 || g.creditedUSD > 0) return 'partial';
  return 'unpaid';
}

// Editing / deleting an already-recorded payment on a 'separate'-mode
// invoice: move `deltaPaid` on the group that payment was recorded against
// (negative = paid less / deleted), then re-aggregate the invoice-level
// scalars from the groups — the same invariant RecordPaymentModal /
// RecordSalePaymentModal keep when a payment is first recorded. Returns
// null if that group no longer exists (caller falls back to the plain
// invoice-level update).
export function shiftDueGroupPaid(groups: DueGroup[], groupId: string, deltaPaid: number): {
  dueGroups: DueGroup[]; paidUSD: number; creditedUSD: number; paymentStatus: 'paid' | 'partial' | 'unpaid'; dueDate: string;
} | null {
  const idx = groups.findIndex(g => g.id === groupId);
  if (idx === -1) return null;
  const moved: DueGroup = { ...groups[idx], paidUSD: round2(Math.max(0, groups[idx].paidUSD + deltaPaid)) };
  moved.paymentStatus = dueGroupStatus(moved);
  const next = [...groups];
  next[idx] = moved;
  return { dueGroups: next, ...aggregateFromDueGroups(next) };
}
